import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, viewChild } from '@angular/core';
import type { JuiImageEntry, JuiImageRejection } from '@jump-tech-frontend/core-components/components';
import { JuiImageCapture } from '@jump-tech-frontend/core-components-angular';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { QuestionExampleImagesComponent } from '../../components/question-example-images.component';
import { FormUpdate } from '../question.model';
import { OcrCaptureQuestionParams } from './ocr-capture.model';
import { OcrCapturePresenter } from './ocr-capture.presenter';

@Component({
  selector: 'question-ocr-capture',
  templateUrl: './ocr-capture.component.html',
  styleUrls: ['./ocr-capture.component.scss'],
  imports: [JuiImageCapture, QuestionExampleImagesComponent, NgxSpinnerModule],
  providers: [OcrCapturePresenter],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OcrCaptureComponent {
  private readonly presenter = inject(OcrCapturePresenter);
  private readonly spinner = inject(NgxSpinnerService);
  private readonly control = viewChild.required(JuiImageCapture);

  public readonly params = input.required<OcrCaptureQuestionParams>();
  public readonly formChange = output<FormUpdate>();

  public readonly vm = this.presenter.vm;
  public readonly spinnerName = computed(() => `${this.params().key}_ocr`);

  constructor() {
    this.presenter.onFormChange(update => this.formChange.emit(update));
    effect(() => this.presenter.load(this.params()));
    effect(() => {
      if (this.vm().reading) {
        void this.spinner.show(this.spinnerName());
      } else {
        void this.spinner.hide(this.spinnerName());
      }
    });
  }

  public onImagesChange(event: CustomEvent<JuiImageEntry[]>): void {
    void this.presenter.imagesChanged(event.detail);
  }

  public onReject(event: CustomEvent<JuiImageRejection>): void {
    this.presenter.rejected(event.detail);
  }

  public onCaptureRequest(event: CustomEvent<void>): void {
    if (!this.presenter.offersCamera()) {
      return;
    }
    event.preventDefault();
    void this.captureWithCamera();
  }

  private async captureWithCamera(): Promise<void> {
    const photo = await this.presenter.captureWithCamera(() => void this.control().openPicker());
    if (photo) {
      await this.control().addImages([photo]);
    }
  }
}
